import { useState, useEffect, useRef } from 'preact/hooks';
import { signal } from '@preact/signals';
import { colorProfile, getProfile } from '../state/color-profile.js';

export const shareCardRequest = signal<ShareCardMode | null>(null);
import {
  renderVerseCard,
  renderMilestoneCard,
  type CardTheme,
  type CardFormat,
} from '../share/card-composer.js';

export type ShareCardMode =
  | { kind: 'verse'; verseText: string; reference: string }
  | { kind: 'milestone'; icon: string; label: string };

type Props = {
  mode: ShareCardMode;
  onClose: () => void;
};

function profileToTheme(id: string): CardTheme {
  const p = getProfile(id as Parameters<typeof getProfile>[0]);
  return p.scheme === 'dark' ? 'dark' : 'light';
}

export function ShareCardSheet({ mode, onClose }: Props) {
  const [theme, setTheme] = useState<CardTheme>(() => profileToTheme(colorProfile.value));
  const [format, setFormat] = useState<CardFormat>('portrait');
  const [blob, setBlob] = useState<Blob | null>(null);
  const [rendering, setRendering] = useState(true);
  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    setRendering(true);
    let cancelled = false;

    const render = mode.kind === 'verse'
      ? renderVerseCard({ verseText: mode.verseText, reference: mode.reference, theme, format })
      : renderMilestoneCard({ icon: mode.icon, label: mode.label, subtitle: 'Achievement Unlocked', theme, format });

    render.then((b) => {
      if (!cancelled) {
        setBlob(b);
        setRendering(false);
      }
    }).catch(() => {
      if (!cancelled) setRendering(false);
    });

    return () => { cancelled = true; };
  }, [theme, format, mode]);

  useEffect(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    if (blob) {
      blobUrlRef.current = URL.createObjectURL(blob);
    }
  }, [blob]);

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const blobUrl = blobUrlRef.current;

  const handleDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = 'scripture-card.png';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleShare = async () => {
    if (!blob) return;
    const nav = navigator as Navigator & {
      share?: (data: ShareData & { files?: File[] }) => Promise<void>;
      canShare?: (data: ShareData & { files?: File[] }) => boolean;
    };
    if (!nav.share) return;
    const file = new File([blob], 'scripture-card.png', { type: 'image/png' });
    if (nav.canShare && !nav.canShare({ files: [file] })) return;
    try {
      await nav.share({ files: [file] });
    } catch {
      /* abort or unsupported — ignore */
    }
  };

  const canShareFile = (() => {
    const nav = navigator as Navigator & {
      share?: unknown;
      canShare?: (data: { files?: File[] }) => boolean;
    };
    if (!nav.share) return false;
    if (!blob) return false;
    if (!nav.canShare) return true;
    try {
      return nav.canShare({ files: [new File([blob], 'scripture-card.png', { type: 'image/png' })] });
    } catch {
      return false;
    }
  })();

  const onScrim = (e: MouseEvent) => {
    if ((e.target as HTMLElement) === (e.currentTarget as HTMLElement)) onClose();
  };

  return (
    <div class="modal-scrim" onClick={onScrim}>
      <div class="modal-sheet">
        <div class="modal-sheet-grabber" />
        <div class="modal-sheet-header">
          <span class="modal-sheet-title">Share Card</span>
          <button type="button" class="modal-sheet-close" onClick={onClose} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div class="modal-sheet-body">
          <div class="share-card-preview-wrap">
            {rendering || !blobUrl ? (
              <div class="share-card-loading" aria-label="Rendering card">…</div>
            ) : (
              <img class="share-card-preview" src={blobUrl} alt="Scripture card preview" />
            )}
          </div>

          <div class="share-card-pickers">
            <div class="share-card-picker-row">
              <span class="share-card-picker-label">Format</span>
              <div class="seg-ctrl">
                <button
                  type="button"
                  class={`seg-ctrl-opt${format === 'portrait' ? ' is-active' : ''}`}
                  onClick={() => setFormat('portrait')}
                >
                  Portrait
                </button>
                <button
                  type="button"
                  class={`seg-ctrl-opt${format === 'landscape' ? ' is-active' : ''}`}
                  onClick={() => setFormat('landscape')}
                >
                  Landscape
                </button>
              </div>
            </div>
            <div class="share-card-picker-row">
              <span class="share-card-picker-label">Theme</span>
              <div class="seg-ctrl">
                <button
                  type="button"
                  class={`seg-ctrl-opt${theme === 'light' ? ' is-active' : ''}`}
                  onClick={() => setTheme('light')}
                >
                  Light
                </button>
                <button
                  type="button"
                  class={`seg-ctrl-opt${theme === 'dark' ? ' is-active' : ''}`}
                  onClick={() => setTheme('dark')}
                >
                  Dark
                </button>
              </div>
            </div>
          </div>

          <div class="share-card-actions">
            <button
              type="button"
              class="share-card-btn"
              onClick={handleDownload}
              disabled={!blobUrl || rendering}
            >
              Download
            </button>
            {canShareFile ? (
              <button
                type="button"
                class="share-card-btn share-card-btn--primary"
                onClick={() => void handleShare()}
                disabled={!blob || rendering}
              >
                Share
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
